import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check } from 'lucide-react';
import StudioCarousel from '../components/StudioCarousel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Studios = () => {
  const [studios, setStudios] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [busySlots, setBusySlots] = useState([]);
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    booking_date: '',
    booking_time: '',
    hours: 1,
    pax: 1,
    rental_purpose: '',
    notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudios();
    fetchPricing();
  }, []);

  const fetchStudios = async () => {
    try {
      const response = await axios.get(`${API}/studios`);
      setStudios(response.data);
    } catch (error) {
      console.error('Error fetching studios:', error);
      toast.error('Failed to load studios');
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/studio-pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleBookStudio = (studio) => {
    setSelectedStudio(studio);
    setBusySlots([]);
    if (bookingData.booking_date) fetchAvailability(bookingData.booking_date);
  };

  const handleInputChange = (e) => {
    const next = { ...bookingData, [e.target.name]: e.target.value };
    setBookingData(next);
    if (e.target.name === 'booking_date') {
      fetchAvailability(e.target.value);
    }
  };

  const fetchAvailability = async (date) => {
    if (!date) { setBusySlots([]); return; }
    try {
      const response = await axios.get(`${API}/studio/availability`, { params: { date } });
      setBusySlots(response.data.busy || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setBusySlots([]);
    }
  };

  const to12h = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingData.customer_name || !bookingData.customer_email || !bookingData.customer_phone || !bookingData.booking_date || !bookingData.booking_time || !bookingData.rental_purpose.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/bookings`, {
        ...bookingData,
        hours: Number(bookingData.hours),
        pax: Number(bookingData.pax),
        booking_type: 'studio',
        studio_id: selectedStudio.id
      });

      toast.success('Booking created! Redirecting to payment...');
      setTimeout(() => {
        navigate(`/payment/${response.data.id}`);
      }, 1000);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const getHourlyRate = (pax, hours) => {
    if (!pricing) return 0;
    const p = Number(pax) || 1;
    const h = Number(hours) || 1;
    const bulk = h >= pricing.bulk_hours;
    if (p <= pricing.tier1_max) return bulk ? pricing.tier1_rate_bulk : pricing.tier1_rate;
    if (p <= pricing.tier2_max) return bulk ? pricing.tier2_rate_bulk : pricing.tier2_rate;
    return bulk ? pricing.tier3_rate_bulk : pricing.tier3_rate;
  };

  const calculateTotal = () => {
    const rate = getHourlyRate(bookingData.pax, bookingData.hours);
    return rate * (Number(bookingData.hours) || 1);
  };

  const peso = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH')}`;

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-16" data-testid="studios-header">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            BOOK YOUR SPACE
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-600">
            Book our professional studio{studios.length === 1 ? '' : 's'} for your rehearsals, practice, and performances
          </p>
        </div>

        {/* Studios Grid */}
        <div className="flex flex-col md:flex-row flex-wrap justify-center border border-zinc-200" data-testid="studios-grid">
          {studios.map((studio) => (
            <div
              key={studio.id}
              className="flex-1 min-w-[300px] group relative overflow-hidden border-b md:border-b-0 md:border-r border-zinc-200 last:border-r-0 hover:bg-zinc-50 transition-all"
              data-testid={`studio-card-${studio.id}`}
            >
              <StudioCarousel
                images={(studio.images && studio.images.length > 0) ? studio.images : (studio.image_url ? [studio.image_url] : [])}
                alt={studio.name}
                testid={`studio-carousel-${studio.id}`}
              />
              
              <div className="p-8">
                <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
                  {studio.name}
                </h2>
                <p className="text-base text-zinc-600 mb-6">
                  {studio.description}
                </p>

                <div className="mb-6 space-y-2">
                  <div className="text-sm font-medium uppercase tracking-widest text-zinc-400 mb-3">
                    Amenities
                  </div>
                  {studio.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2 text-zinc-700">
                      <Check size={16} className="text-[var(--accent)]" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>

                <div className="mb-6 pb-6 border-b border-zinc-200">
                  <div className="text-sm font-medium uppercase tracking-widest text-zinc-400">
                    Group-based pricing
                  </div>
                  <div className="text-2xl font-bold mt-1" style={{ fontFamily: 'var(--heading-font)' }}>
                    From {peso(pricing?.tier1_rate_bulk || 1000)}<span className="text-base font-normal text-zinc-600">/hour</span>
                  </div>
                </div>

                <button
                  onClick={() => handleBookStudio(studio)}
                  className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-4 text-lg font-bold transition-transform hover:scale-105 active:scale-95"
                  data-testid={`reserve-studio-btn-${studio.id}`}
                >
                  BOOK YOUR SPACE
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Tiers */}
        {pricing && (
          <div className="mt-12 border border-zinc-200 p-8" data-testid="pricing-tiers">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: 'var(--heading-font)' }}>
              Studio Rates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border border-zinc-200 p-6" data-testid="tier-1">
                <p className="text-sm font-medium uppercase tracking-widest text-zinc-400 mb-2">1 – {pricing.tier1_max} pax</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{peso(pricing.tier1_rate)}<span className="text-base font-normal text-zinc-600">/hr</span></p>
                <p className="text-zinc-600 mt-1">{peso(pricing.tier1_rate_bulk)}/hr if {pricing.bulk_hours} hours or more</p>
              </div>
              <div className="border border-zinc-200 p-6" data-testid="tier-2">
                <p className="text-sm font-medium uppercase tracking-widest text-zinc-400 mb-2">{pricing.tier1_max + 1} – {pricing.tier2_max} pax</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{peso(pricing.tier2_rate)}<span className="text-base font-normal text-zinc-600">/hr</span></p>
                <p className="text-zinc-600 mt-1">{peso(pricing.tier2_rate_bulk)}/hr if {pricing.bulk_hours} hours or more</p>
              </div>
              <div className="border border-zinc-200 p-6" data-testid="tier-3">
                <p className="text-sm font-medium uppercase tracking-widest text-zinc-400 mb-2">{pricing.tier2_max}+ pax</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{peso(pricing.tier3_rate)}<span className="text-base font-normal text-zinc-600">/hr</span></p>
                <p className="text-zinc-600 mt-1">{peso(pricing.tier3_rate_bulk)}/hr if {pricing.bulk_hours} hours or more</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={!!selectedStudio} onOpenChange={() => setSelectedStudio(null)}>
        <DialogContent className="max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              Book {selectedStudio?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="customer_name">Full Name *</Label>
              <Input
                id="customer_name"
                name="customer_name"
                value={bookingData.customer_name}
                onChange={handleInputChange}
                required
                data-testid="booking-name-input"
              />
            </div>

            <div>
              <Label htmlFor="customer_email">Email *</Label>
              <Input
                id="customer_email"
                name="customer_email"
                type="email"
                value={bookingData.customer_email}
                onChange={handleInputChange}
                required
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <Label htmlFor="customer_phone">Phone Number *</Label>
              <Input
                id="customer_phone"
                name="customer_phone"
                value={bookingData.customer_phone}
                onChange={handleInputChange}
                required
                data-testid="booking-phone-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="booking_date">Date *</Label>
                <Input
                  id="booking_date"
                  name="booking_date"
                  type="date"
                  value={bookingData.booking_date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="booking-date-input"
                />
              </div>

              <div>
                <Label htmlFor="booking_time">Time *</Label>
                <Input
                  id="booking_time"
                  name="booking_time"
                  type="time"
                  value={bookingData.booking_time}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-time-input"
                />
              </div>
            </div>

            {bookingData.booking_date && (
              <div className="rounded border border-zinc-200 p-3" data-testid="availability-box">
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-400 mb-2">
                  Unavailable on {bookingData.booking_date}
                </div>
                {busySlots.length === 0 ? (
                  <p className="text-sm text-green-700" data-testid="availability-free">The studio is free all day — pick any time.</p>
                ) : (
                  <ul className="space-y-1" data-testid="availability-busy-list">
                    {busySlots.map((s, i) => (
                      <li key={i} className="flex items-center justify-between text-sm" data-testid={`busy-slot-${i}`}>
                        <span className="text-zinc-700">{to12h(s.start)} – {to12h(s.end)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${s.type === 'class' ? 'bg-zinc-900 text-white' : 'bg-amber-100 text-amber-700'}`}>
                          {s.type === 'class' ? `Class: ${s.label}` : s.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hours">Number of Hours *</Label>
                <Input
                  id="hours"
                  name="hours"
                  type="number"
                  min="1"
                  max="12"
                  value={bookingData.hours}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-hours-input"
                />
              </div>
              <div>
                <Label htmlFor="pax">Number of People (pax) *</Label>
                <Input
                  id="pax"
                  name="pax"
                  type="number"
                  min="1"
                  value={bookingData.pax}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-pax-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="rental_purpose">Purpose of Rental *</Label>
              <Textarea
                id="rental_purpose"
                name="rental_purpose"
                value={bookingData.rental_purpose}
                onChange={handleInputChange}
                placeholder="e.g. Dance practice for a competition, content shoot, workshop"
                maxLength={500}
                rows={2}
                required
                data-testid="booking-rental-purpose-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                name="notes"
                value={bookingData.notes}
                onChange={handleInputChange}
                data-testid="booking-notes-input"
              />
            </div>

            <div className="bg-zinc-50 p-4 rounded space-y-1" data-testid="booking-total-box">
              <div className="flex justify-between items-center text-sm text-zinc-600">
                <span>Rate ({bookingData.pax || 1} pax{Number(bookingData.hours) >= (pricing?.bulk_hours || 3) ? `, ${pricing?.bulk_hours || 3}h+` : ''}):</span>
                <span data-testid="booking-hourly-rate">{peso(getHourlyRate(bookingData.pax, bookingData.hours))}/hr × {Number(bookingData.hours) || 1}h</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Amount:</span>
                <span className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="booking-total-amount">
                  {peso(calculateTotal())}
                </span>
              </div>
            </div>

            <div className="pt-4 space-y-2">
              <button
                type="submit"
                className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95"
                data-testid="confirm-booking-btn"
              >
                PROCEED TO PAYMENT
              </button>
              <button
                type="button"
                onClick={() => setSelectedStudio(null)}
                className="w-full border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors"
                data-testid="cancel-booking-btn"
              >
                CANCEL
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Studios;